import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, SunIcon, MoonIcon } from 'lucide-react';
interface NavbarProps {
  isDark: boolean;
  toggleTheme: () => void;
}
export function Navbar({
  isDark,
  toggleTheme
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Services',
    href: '/#services'
  }, {
    name: 'About',
    href: '/#about'
  }, {
    name: 'Contact',
    href: '/#contact'
  }];
  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 dark:bg-[#0f1629]/90 backdrop-blur-md border-b border-slate-200 dark:border-white/10 py-3 shadow-sm dark:shadow-none' : 'bg-transparent py-5'}`} data-id="element-133">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134">
        <div className="flex justify-between items-center" data-id="element-135">
          {/* Logo */}
          <Link to="/" className="flex items-center group -my-1" data-id="element-136">
            <img src="https://cdn.magicpatterns.com/uploads/2ta7BLC2bm2HydpkJQJwcY/Logo_Transparent.png" alt="Scale Finance Strategies" className={`w-auto object-contain dark:brightness-0 dark:invert transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`} data-id="element-137" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-138">
            <Link to="/#services" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-139">
              Services
            </Link>

            <Link to="/#about" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-140">
              About
            </Link>

            <Link to="/#contact" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-141">
              Contact
            </Link>

            <div className="flex items-center gap-3 pl-6 border-l border-slate-200 dark:border-white/10" data-id="element-142">
              <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-143">
                {isDark ? <SunIcon className="w-5 h-5" data-id="element-144" /> : <MoonIcon className="w-5 h-5" data-id="element-145" />}
              </button>
              <Link to="/#contact" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-accent-500 to-primary-500 text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02]" data-id="element-146">
                Book a Strategy Session
              </Link>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center gap-3" data-id="element-147">
            <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-148">
              {isDark ? <SunIcon className="w-5 h-5" data-id="element-149" /> : <MoonIcon className="w-5 h-5" data-id="element-150" />}
            </button>
            <button className="p-2 text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-151">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-152" /> : <MenuIcon className="w-6 h-6" data-id="element-153" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-[#0f1629]/95 backdrop-blur-xl border-b border-slate-200 dark:border-white/10 py-4 px-4 flex flex-col gap-2 shadow-xl max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-154">
          <Link to="/#services" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-155">
            Services
          </Link>

          <Link to="/#about" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-156">
            About
          </Link>

          <Link to="/#contact" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-157">
            Contact
          </Link>

          <Link to="/#contact" className="mt-4 text-center px-5 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold shadow-md" onClick={() => setIsMobileMenuOpen(false)} data-id="element-158">
            Book a Strategy Session
          </Link>
        </div>}
    </nav>;
}